import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  AutocompleteItem,
  Button,
  Card,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Skeleton,
  Table,
  TableColumn,
  TableHeader,
  type SharedSelection,
} from '@/components/ui';
import {
  DataTable,
  type Column,
  type Pagination,
} from '@/components/common/table';
import { StandardIcon } from '@/components/common/icon';
import { SheepfoldAutocomplete } from '@/components/common/input/SheepfoldAutocomplete';
import { useApiWithAuth } from '@/hooks/api';
import { useUserAccess } from '@/hooks/auth';
import type { ApiResponse } from '@/shared/types/api';
import type {
  GroupsSyncResult,
  PcoGroupLeaderRef,
} from '@/shared/types/pco/group';
import type { Group } from '@/shared/types/group';
import { useGroups } from '@/store/people/hooks';
import { Icon } from '@iconify/react';

interface GroupTypeItem {
  pco_id: string;
  name: string;
}

interface GroupItem {
  pco_id: string;
  name: string;
  pco_group_type_id: string | null;
  memberships_count: number;
  contact_email: string | null;
  schedule: string | null;
  mhx_parent_id?: string | null;
  mhx_group_leaders?: PcoGroupLeaderRef[];
  mhx_assistant_group_leaders?: PcoGroupLeaderRef[];
  mhx_group_leader_user_ids?: string[];
  mhx_assistant_group_leader_user_ids?: string[];
  mhx_campus_pco_id?: string | null;
  pco_campus_ids?: string[];
}

interface GroupRow {
  id: string;
  data: GroupItem;
}

interface GroupMemberPersonItem {
  pco_person_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
}

function personDisplayName(item: GroupMemberPersonItem): string {
  const full = `${item.first_name ?? ''} ${item.last_name ?? ''}`.trim();
  if (full) {
    return full;
  }

  return item.email?.trim() || item.pco_person_id;
}

function personNameOnly(item: GroupMemberPersonItem): string {
  return `${item.first_name ?? ''} ${item.last_name ?? ''}`.trim();
}

function leaderSlotsToPcoPersonIds(slots?: PcoGroupLeaderRef[]): string[] {
  if (!slots?.length) {
    return [];
  }

  return [
    ...new Set(
      slots
        .map((s) => String(s.pco_person_id ?? '').trim())
        .filter((id) => id.length > 0),
    ),
  ];
}

function PcoMemberMultiSelect({
  label,
  selected,
  onChange,
  members,
  loading,
}: {
  label: string;
  selected: string[];
  onChange: (ids: string[]) => void;
  members: GroupMemberPersonItem[] | null;
  loading: boolean;
}) {
  const byId = useMemo(
    () => new Map((members ?? []).map((m) => [m.pco_person_id, m])),
    [members],
  );
  const available = useMemo(
    () => (members ?? []).filter((m) => !selected.includes(m.pco_person_id)),
    [members, selected],
  );
  const [input, setInput] = useState('');

  return (
    <Card className='border-1 border-gray-200 px-3 py-2' shadow='none'>
      <label className='text-[75%] font-medium text-foreground-600'>
        {label}
      </label>
      <div className='mt-2 space-y-1'>
        {selected.map((id) => {
          const person = byId.get(id);
          const primary = person
            ? personNameOnly(person) || `PCO Person ${id}`
            : `PCO Person ${id}`;
          const subtext = person?.email?.trim() || null;
          return (
            <div key={id} className='flex items-start gap-2'>
              <span className='grow'>
                <span className='block text-sm text-slate-700'>{primary}</span>
                {subtext ? (
                  <span className='block text-xs text-slate-500'>
                    {subtext}
                  </span>
                ) : null}
              </span>
              <Button
                isIconOnly
                variant='flat'
                className='bg-transparent text-gray-500'
                onPress={() => onChange(selected.filter((x) => x !== id))}
              >
                ×
              </Button>
            </div>
          );
        })}
      </div>
      <Autocomplete
        className='mt-2'
        defaultItems={available}
        isLoading={loading}
        listboxProps={{ portal: true }}
        inputValue={input}
        onInputChange={setInput}
        placeholder='Search group members (PCO people)…'
        selectedKey={null}
        onSelectionChange={(key) => {
          const id = String(key ?? '').trim();
          if (!id || selected.includes(id)) {
            return;
          }
          onChange([...selected, id]);
          setInput('');
        }}
      >
        {(item) => (
          <AutocompleteItem
            key={item.pco_person_id}
            textValue={personDisplayName(item)}
          >
            <div className='flex flex-col'>
              <span>
                {personNameOnly(item) || `PCO Person ${item.pco_person_id}`}
              </span>
              {item.email ? (
                <span className='text-xs text-slate-500'>{item.email}</span>
              ) : null}
            </div>
          </AutocompleteItem>
        )}
      </Autocomplete>
    </Card>
  );
}

/** Client-side timeout so loading never hangs if the server doesn't respond */
const REQUEST_TIMEOUT_MS = 30_000;

/** PCO full groups sync can take several minutes (many HTTP calls to Planning Center). */
const GROUPS_SYNC_TIMEOUT_MS = 600_000;

function withRequestTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('Request timed out')), ms),
    ),
  ]);
}

const GROUPS_COLUMNS = [
  { key: 'name', label: 'Group' },
  { key: 'type', label: 'Type' },
  { key: 'parent', label: 'Sheepfold section' },
  { key: 'members', label: 'Members' },
  { key: 'email', label: 'Contact Email' },
  { key: 'schedule', label: 'Schedule' },
  { key: 'actions', label: '' },
];

function resolveMhxParentFromSheepfoldSelection(
  selectedId: string,
  groupList: Group[],
): string {
  if (!selectedId.trim()) {
    return '';
  }

  const g = groupList.find((x) => x.id === selectedId);
  if (!g) {
    return '';
  }

  if (g.data.type === 'laygroup1') {
    return g.id;
  }

  if (g.data.type === 'laygroup2' && g.data.parent_id) {
    return g.data.parent_id;
  }

  return '';
}

const SKELETON_ROW_COUNT = 5;

function GroupsTableSkeleton({ columns }: { columns: typeof GROUPS_COLUMNS }) {
  return (
    <Table isHeaderSticky className='max-h-[calc(100vh-220px)] text-gray-800'>
      <TableHeader columns={columns}>
        {(col) => <TableColumn key={col.key}>{col.label}</TableColumn>}
      </TableHeader>
      <tbody className='divide-y divide-slate-100'>
        {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
          <tr key={i}>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-48 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-24 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-12 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-36 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-28 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-24 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-8 w-16 rounded-md' />
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export default function AccessGroups() {
  const { invoke, auth } = useApiWithAuth();
  const userAccess = useUserAccess();
  const isAdminUser = Boolean(userAccess?.admin);
  const invokeRef = useRef(invoke);
  invokeRef.current = invoke;
  const { list: sheepfoldGroupList } = useGroups(
    undefined,
    undefined,
    undefined,
  );
  const [groupTypes, setGroupTypes] = useState<GroupTypeItem[]>([]);
  /** Raw key from Select (may be React-prefixed e.g. "1:$12898"); use selectedGroupTypeId for API */
  const [selectedGroupTypeKey, setSelectedGroupTypeKey] = useState('');
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const groupsRequestKey = useRef('');
  const [filterSheepfoldId, setFilterSheepfoldId] = useState('');
  const filterMhxParentId = useMemo(
    () =>
      resolveMhxParentFromSheepfoldSelection(
        filterSheepfoldId,
        sheepfoldGroupList,
      ),
    [filterSheepfoldId, sheepfoldGroupList],
  );
  const laygroup1Lookup = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of sheepfoldGroupList) {
      if (g.data.type === 'laygroup1') {
        m.set(g.id, g.data.title || g.id);
      }
    }
    return m;
  }, [sheepfoldGroupList]);

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState<GroupRow | null>(null);
  const [editParentId, setEditParentId] = useState('');
  const [editGlPcoIds, setEditGlPcoIds] = useState<string[]>([]);
  const [editAglPcoIds, setEditAglPcoIds] = useState<string[]>([]);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  /** null=loading, []/array=loaded, undefined=closed or failed */
  const [editMemberPeople, setEditMemberPeople] = useState<
    GroupMemberPersonItem[] | null | undefined
  >(undefined);
  const [syncing, setSyncing] = useState(false);
  const [syncNotice, setSyncNotice] = useState<{
    color: 'success' | 'danger' | 'warning';
    text: string;
  } | null>(null);

  /** pco_id for API; strips React Select key prefix (e.g. "1:$12898" -> "12898") */
  const selectedGroupTypeId = useMemo(() => {
    const raw = selectedGroupTypeKey;
    if (!raw) {
      return '';
    }
    return raw.includes(':$') && raw.length > raw.indexOf(':$') + 1
      ? raw.split(':$').pop()!
      : raw;
  }, [selectedGroupTypeKey]);

  const groupTypeLookup = useMemo(() => {
    return groupTypes.reduce(
      (lookup, item) => {
        lookup[item.pco_id] = item.name;

        return lookup;
      },
      {} as Record<string, string>,
    );
  }, [groupTypes]);

  const columns = useMemo(
    (): Column<GroupRow>[] => [
      {
        key: 'name',
        label: 'Group',
        display: ({ item }) => (
          <span className='font-medium text-slate-800'>{item.data.name}</span>
        ),
      },
      {
        key: 'type',
        label: 'Type',
        display: ({ item }) => {
          const typeId = item.data.pco_group_type_id;
          const label = typeId ? groupTypeLookup[typeId] : null;
          if (label === null || label === undefined) {
            return <></>;
          }
          return (
            <Chip className='rounded-md border border-slate-200 bg-slate-100 px-3 py-1.5 text-xs text-slate-700'>
              {label}
            </Chip>
          );
        },
      },
      {
        key: 'parent',
        label: 'Sheepfold section',
        display: ({ item }) => {
          const pid = item.data.mhx_parent_id;
          if (!pid) {
            return <span className='text-slate-400'>—</span>;
          }
          return (
            <span className='text-slate-700'>
              {laygroup1Lookup.get(pid) ?? pid}
            </span>
          );
        },
      },
      {
        key: 'members',
        label: 'Members',
        display: ({ item }) => <span>{item.data.memberships_count}</span>,
      },
      {
        key: 'email',
        label: 'Contact Email',
        display: ({ item }) => <span>{item.data.contact_email || '-'}</span>,
      },
      {
        key: 'schedule',
        label: 'Schedule',
        display: ({ item }) => <span>{item.data.schedule || '-'}</span>,
      },
      {
        key: 'actions',
        label: '',
        display: ({ item }) => (
          <Button
            size='sm'
            variant='flat'
            onPress={() => {
              setEditRow(item);
              setEditParentId(item.data.mhx_parent_id?.trim() || '');
              setEditGlPcoIds(
                leaderSlotsToPcoPersonIds(item.data.mhx_group_leaders),
              );
              setEditAglPcoIds(
                leaderSlotsToPcoPersonIds(
                  item.data.mhx_assistant_group_leaders,
                ),
              );
              setEditError('');
              setEditMemberPeople(null);
              setEditOpen(true);
            }}
          >
            Edit
          </Button>
        ),
      },
    ],
    [groupTypeLookup, laygroup1Lookup],
  );

  useEffect(() => {
    if (!auth.ready) {
      return undefined;
    }

    let cancelled = false;

    const loadGroupTypes = async () => {
      try {
        const response = await withRequestTimeout(
          invokeRef.current('/pco/groups/types') as Promise<
            ApiResponse<GroupTypeItem[]>
          >,
          REQUEST_TIMEOUT_MS,
        );
        if (cancelled) {
          return;
        }
        // Handle both successful JSON { data: T } and error/non-JSON response
        const list =
          response !== null &&
          response !== undefined &&
          typeof response === 'object' &&
          'data' in response &&
          Array.isArray((response as ApiResponse<GroupTypeItem[]>).data)
            ? (response as ApiResponse<GroupTypeItem[]>).data
            : [];
        setGroupTypes(list);
      } catch (err) {
        if (cancelled) {
          return;
        }

        console.error('Failed loading group types:', err);
        setGroupTypes([]);
      }
    };

    loadGroupTypes();

    return () => {
      cancelled = true;
    };
  }, [auth.ready]);

  useEffect(() => {
    if (!auth.ready) {
      setLoading(false);
      return undefined;
    }

    const requestKey = `${auth.firebaseUser?.uid ?? ''}|${selectedGroupTypeId}|${filterMhxParentId}|${reloadToken}`;
    if (groupsRequestKey.current === requestKey) {
      // Already loading or loaded for this key (e.g. Strict Mode re-run skipped).
      // Ensure we don't leave loading true forever from a cancelled prior run.
      setLoading(false);
      return undefined;
    }

    groupsRequestKey.current = requestKey;
    let cancelled = false;

    const loadGroups = async () => {
      setLoading(true);
      setError('');

      try {
        const params = new URLSearchParams();
        params.set('limit', '500');
        if (selectedGroupTypeId) {
          params.set('groupTypeId', selectedGroupTypeId);
        }

        if (filterMhxParentId) {
          params.set('mhxParentId', filterMhxParentId);
        }

        const response = await withRequestTimeout(
          invokeRef.current(`/pco/groups?${params.toString()}`) as Promise<
            ApiResponse<GroupItem[]>
          >,
          REQUEST_TIMEOUT_MS,
        );
        if (cancelled) {
          return;
        }

        if (response && typeof response === 'object' && 'data' in response) {
          setGroups(
            (response.data || []).map((item) => ({
              id: item.pco_id,
              data: item,
            })),
          );
        } else {
          setGroups([]);
        }
        setPage(1);
      } catch (err) {
        if (cancelled) {
          return;
        }

        console.error('Failed loading groups:', err);
        setGroups([]);
        setError(
          err instanceof Error && err.message === 'Request timed out'
            ? 'Request timed out. Try again or use Reload.'
            : 'Unable to load groups',
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadGroups();

    return () => {
      cancelled = true;
      // Reset so a re-run (e.g. React Strict Mode) or later mount will load again
      groupsRequestKey.current = '';
    };
  }, [
    auth.ready,
    auth.firebaseUser?.uid,
    selectedGroupTypeId,
    filterMhxParentId,
    reloadToken,
  ]);

  const editPcoIdForMembers = editRow?.data.pco_id;

  useEffect(() => {
    if (!editOpen || !editPcoIdForMembers || !auth.ready) {
      return undefined;
    }

    const pcoId = editPcoIdForMembers;
    let cancelled = false;

    const loadMemberPeople = async () => {
      try {
        const response = await withRequestTimeout(
          invokeRef.current(
            `/pco/groups/${encodeURIComponent(pcoId)}/member-people`,
          ) as Promise<ApiResponse<GroupMemberPersonItem[]>>,
          REQUEST_TIMEOUT_MS,
        );
        if (cancelled) {
          return;
        }

        const rawPeople =
          response &&
          typeof response === 'object' &&
          'data' in response &&
          Array.isArray(response.data)
            ? response.data
            : [];
        const normalized = rawPeople
          .map((item) => ({
            pco_person_id: String(item.pco_person_id ?? '').trim(),
            first_name: String(item.first_name ?? '').trim(),
            last_name: String(item.last_name ?? '').trim(),
            email: item.email ? String(item.email).trim().toLowerCase() : null,
          }))
          .filter((item) => item.pco_person_id.length > 0);
        setEditMemberPeople(normalized);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed loading group member people:', err);
          setEditMemberPeople(undefined);
        }
      }
    };

    void loadMemberPeople();

    return () => {
      cancelled = true;
    };
  }, [editOpen, editPcoIdForMembers, auth.ready]);

  const onSyncFromPco = useCallback(async () => {
    if (!isAdminUser) {
      return;
    }

    setSyncNotice(null);
    setSyncing(true);
    try {
      const raw = await withRequestTimeout(
        invoke('/pco/groups/sync', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            database: 'mongodb',
            syncMemberships: true,
            includeArchived: false,
          }),
        }) as Promise<unknown>,
        GROUPS_SYNC_TIMEOUT_MS,
      );

      if (typeof raw === 'string') {
        setSyncNotice({
          color: 'danger',
          text: raw.trim() || 'Sync was rejected or failed.',
        });

        return;
      }

      const response = raw as ApiResponse<GroupsSyncResult | null>;
      const result = response?.data;
      if (result && typeof result === 'object' && 'groups' in result) {
        const summary = [
          `Group types ${result.groupTypes.synced}/${result.groupTypes.total}`,
          `Groups ${result.groups.synced}/${result.groups.total}`,
          `Memberships ${result.memberships.synced}/${result.memberships.total}`,
        ].join(' · ');
        const errCount = result.errors?.length ?? 0;
        let noticeColor: 'success' | 'danger' | 'warning' = 'warning';
        if (result.success) {
          noticeColor = 'success';
        } else if (errCount > 0) {
          noticeColor = 'danger';
        }
        const errSuffix =
          errCount > 0 ? ` ${errCount} error(s) logged on the server.` : '';
        setSyncNotice({
          color: noticeColor,
          text: `${response.message ?? 'Sync finished'}. ${summary}.${errSuffix}`,
        });
        setReloadToken((t) => t + 1);
      } else {
        setSyncNotice({
          color: 'danger',
          text: response?.message ?? 'Sync failed.',
        });
      }
    } catch (e) {
      let failText = 'Sync failed.';
      if (e instanceof Error) {
        failText =
          e.message === 'Request timed out'
            ? 'Sync timed out. It may still be running on the server; try Reload in a few minutes.'
            : e.message;
      }
      setSyncNotice({ color: 'danger', text: failText });
    } finally {
      setSyncing(false);
    }
  }, [invoke, isAdminUser]);

  const onSaveMhx = useCallback(async () => {
    if (!editRow) {
      return;
    }

    setEditSaving(true);
    setEditError('');
    try {
      const parentResolved = resolveMhxParentFromSheepfoldSelection(
        editParentId,
        sheepfoldGroupList,
      );
      await invoke(`/pco/groups/${encodeURIComponent(editRow.data.pco_id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mhx_parent_id: parentResolved || null,
          mhx_group_leaders: editGlPcoIds.map((pco_person_id) => ({
            pco_person_id,
          })),
          mhx_assistant_group_leaders: editAglPcoIds.map((pco_person_id) => ({
            pco_person_id,
          })),
        }),
      });
      setEditOpen(false);
      setEditRow(null);
      setReloadToken((t) => t + 1);
    } catch (e) {
      setEditError(
        e instanceof Error ? e.message : 'Failed to save MinistryHub fields',
      );
    } finally {
      setEditSaving(false);
    }
  }, [
    editRow,
    editParentId,
    editGlPcoIds,
    editAglPcoIds,
    invoke,
    sheepfoldGroupList,
  ]);

  const pagination: Pagination = useMemo(
    () => ({
      enabled: true,
      absolute: true,
      page,
      itemsPerPage,
      allowItemsPerPage: [10, 20, 50, 100],
      onPageChange: (nextPage: number) => setPage(nextPage),
      onItemsPerPageChange: (value: number) => {
        setItemsPerPage(value);
        setPage(1);
      },
    }),
    [page, itemsPerPage],
  );

  const onGroupTypeChange = (selection: SharedSelection) => {
    setSelectedGroupTypeKey(selection.currentKey || '');
  };

  const startIndex = (page - 1) * itemsPerPage;
  const displayedItems = useMemo(
    () => groups.slice(startIndex, startIndex + itemsPerPage),
    [groups, startIndex, itemsPerPage],
  );

  const showSkeleton = loading && groups.length === 0;

  return (
    <div>
      <div className='mb-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center'>
        <Select
          className='min-w-[200px] basis-1/3'
          aria-label='Filter groups by type'
          selectedKeys={new Set([selectedGroupTypeKey])}
          onSelectionChange={onGroupTypeChange}
          placeholder='All Group Types'
        >
          <SelectItem key=''>All Group Types</SelectItem>
          {groupTypes.map((item) => (
            <SelectItem key={item.pco_id}>{item.name}</SelectItem>
          ))}
        </Select>
        <div className='min-w-[220px] flex-1'>
          <SheepfoldAutocomplete
            value={filterSheepfoldId}
            onChange={setFilterSheepfoldId}
            placeholder='All sheepfold sections'
            aria-label='Filter by sheepfold section'
          />
        </div>
        <Button
          variant='light'
          onPress={() => setReloadToken((token) => token + 1)}
          startContent={<StandardIcon id='mdi:reload' />}
        >
          Reload
        </Button>
        {isAdminUser ? (
          <Button
            color='primary'
            variant='flat'
            isDisabled={!auth.ready || syncing}
            isLoading={syncing}
            onPress={() => void onSyncFromPco()}
            startContent={
              syncing ? undefined : <StandardIcon id='mdi:cloud-sync-outline' />
            }
          >
            Sync from PCO
          </Button>
        ) : null}
      </div>
      {syncNotice ? (
        <Alert color={syncNotice.color} className='mb-2'>
          {syncNotice.text}
        </Alert>
      ) : null}
      {error ? (
        <Alert color='danger' className='mb-2'>
          {error}
        </Alert>
      ) : null}
      {showSkeleton ? (
        <GroupsTableSkeleton columns={GROUPS_COLUMNS} />
      ) : (
        <DataTable
          aria-label='Groups'
          columns={columns}
          items={displayedItems}
          count={groups.length}
          pagination={pagination}
          paginationVariant='numbered'
          loading={loading}
          className='text-gray-800'
          empty={{
            icon: 'mdi:account-group-outline',
            bottomLeftIcon: 'material-symbols:group-off-outline',
            title: 'No groups found',
            body: selectedGroupTypeId
              ? "We couldn't find any groups for the selected type. Try choosing a different group type or clear the filter."
              : "We couldn't find any groups. Try reloading or check your connection.",
            actions: (
              <>
                <Button
                  variant='flat'
                  onPress={() => setSelectedGroupTypeKey('')}
                  isDisabled={!selectedGroupTypeKey}
                >
                  Clear filter
                </Button>
                <Button
                  variant='flat'
                  onPress={() => setReloadToken((t) => t + 1)}
                  startContent={
                    <Icon icon='mdi:reload' width={18} height={18} />
                  }
                >
                  Reload
                </Button>
              </>
            ),
          }}
        />
      )}
      <Modal
        isOpen={editOpen}
        onOpenChange={(open) => {
          if (!open) {
            setEditOpen(false);
            setEditRow(null);
            setEditMemberPeople(undefined);
          }
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>MinistryHub — PCO group</ModalHeader>
              <ModalBody className='gap-4'>
                {editRow ? (
                  <p className='text-sm text-slate-600'>
                    <span className='font-semibold'>{editRow.data.name}</span>
                  </p>
                ) : null}
                <SheepfoldAutocomplete
                  value={editParentId}
                  onChange={setEditParentId}
                  label='Sheepfold section (parent)'
                  placeholder='None'
                  aria-label='Select sheepfold section parent'
                />
                {Array.isArray(editMemberPeople) &&
                editMemberPeople.length === 0 ? (
                  <Alert color='warning' className='text-sm'>
                    No PCO group members found in Mongo for this group. Run a
                    groups sync first; leader pickers stay empty until members
                    are synced.
                  </Alert>
                ) : null}
                <PcoMemberMultiSelect
                  key={`gl-pco-${editRow?.id ?? 'none'}`}
                  label='Group leaders'
                  selected={editGlPcoIds}
                  onChange={setEditGlPcoIds}
                  members={editMemberPeople ?? null}
                  loading={editMemberPeople === null}
                />
                <p className='text-xs text-slate-500'>
                  Pickers now store PCO person ids for members in this group.
                  Backend resolution links `mhx_user_id` using the same lookup
                  as the PCO group leader sync.
                </p>
                <PcoMemberMultiSelect
                  key={`agl-pco-${editRow?.id ?? 'none'}`}
                  label='Assistant group leaders'
                  selected={editAglPcoIds}
                  onChange={setEditAglPcoIds}
                  members={editMemberPeople ?? null}
                  loading={editMemberPeople === null}
                />
                {editError ? <Alert color='danger'>{editError}</Alert> : null}
              </ModalBody>
              <ModalFooter>
                <Button variant='flat' onPress={onClose}>
                  Cancel
                </Button>
                <Button
                  color='primary'
                  isLoading={editSaving}
                  onPress={() => void onSaveMhx()}
                >
                  Save
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
